import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Dropdown, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { usePortalApiRequest } from '../../../redux/api/portalApi';
import {
  formatSalesApiError,
  getSalesArrayPayload,
  useSalesEndpoints,
} from '../hooks/useSalesApiResource';
import {
  getFieldValueLabel,
  isNumericFieldType,
  normalizers,
} from './configurationAdminUtils';
import {
  buildStationPartCategoryRows,
  normalizeStationPart,
  normalizeStationPartAssignment,
  normalizeStationPartCategory,
} from './stationPartsUtils';
import {
  buildPartMatchRuleCopyDraft,
  buildPartMatchRuleOperations,
  filterPartAutoSelectCategories,
  findMatchingPartRule,
  getNextPartRuleGroupId,
  getPartMatchRuleGroups,
  getStationAutoPickFieldValues,
  normalizePartMatchRule,
  PART_QUANTITY_MODE_FIXED,
  PART_QUANTITY_MODE_PUMP_ROWS,
  validatePartMatchRuleDraft,
} from './partAutoSelectUtils';
import PartAutoSelectBulkEditor from './PartAutoSelectBulkEditor';
import PumpCellRuleRows from './PumpCellRuleRows';
import { isPartInCategory } from './hcPumpTableRules';
import { isPartRuleGroupRelatedToCell, pumpCellConditions } from './pumpAssignmentUtils';
import {
  analyzePartAutoBulkRows,
  getPartAutoBulkScopeRules,
  getPartAutoBulkSnapshotConflict,
} from './partAutoSelectBulkUtils';

let conditionDraftKey = 0;
const nextConditionKey = () => `part-condition-${conditionDraftKey += 1}`;

const emptyCondition = () => ({
  key: nextConditionKey(),
  id: 0,
  type: 'value',
  configFieldId: 0,
  requiredValueId: 0,
  fieldId: 0,
  minValue: '',
  maxValue: '',
});

const makeDraftCondition = (rule, fieldValueById) => {
  if (rule.requiredValueId) {
    return {
      ...emptyCondition(),
      id: rule.id,
      type: 'value',
      configFieldId: fieldValueById.get(rule.requiredValueId)?.configFieldId || 0,
      requiredValueId: rule.requiredValueId,
    };
  }
  if (!rule.fieldId) {
    return {
      ...emptyCondition(),
      id: rule.id,
      type: 'default',
    };
  }
  return {
    ...emptyCondition(),
    id: rule.id,
    type: 'numeric',
    fieldId: rule.fieldId || 0,
    minValue: rule.minValue ?? '',
    maxValue: rule.maxValue ?? '',
  };
};

const PartAutoSelectManager = ({ station, canEditSales, refreshVersion = 0, cellContext = null, onRulesChanged, onEditorStateChange }) => {
  const endpoints = useSalesEndpoints();
  const request = usePortalApiRequest();
  const [categories, setCategories] = useState([]);
  const [parts, setParts] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [rules, setRules] = useState([]);
  const [stations, setStations] = useState([]);
  const [fields, setFields] = useState([]);
  const [fieldValues, setFieldValues] = useState([]);
  const [configValueRules, setConfigValueRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [editor, setEditor] = useState(null);
  const [validation, setValidation] = useState('');
  const [saving, setSaving] = useState(false);
  const [showFieldBrowser, setShowFieldBrowser] = useState(false);
  const [fieldBrowserSearch, setFieldBrowserSearch] = useState('');
  const [fieldBrowserType, setFieldBrowserType] = useState('all');
  const [selectedBrowserFieldId, setSelectedBrowserFieldId] = useState(0);
  const [partSearch, setPartSearch] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState('all');
  const [bulkEditorOpen, setBulkEditorOpen] = useState(false);
  const [bulkValidation, setBulkValidation] = useState('');
  const [showUnconfiguredParts, setShowUnconfiguredParts] = useState(false);
  useEffect(() => { onEditorStateChange?.(Boolean(editor) || saving); }, [editor, saving, onEditorStateChange]);

  const fetchRules = useCallback(async () => {
    const response = await request({ method: 'GET', url: endpoints.partMatchRules.base });
    return getSalesArrayPayload(response).map(normalizePartMatchRule);
  }, [endpoints, request]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Keep these requests sequential: this page needs only its assigned-part
      // pool and rule references, and the Sales API is sensitive to bursts.
      const assignmentResponse = await request({
        method: 'GET',
        url: endpoints.partStations.base,
        params: { stationid: station.id },
      });
      const nextAssignments = getSalesArrayPayload(assignmentResponse)
        .map(normalizeStationPartAssignment)
        .filter((assignment) => assignment.stationId === station.id);
      setAssignments(nextAssignments);

      const partResponse = await request({ method: 'GET', url: endpoints.parts.base });
      setParts(getSalesArrayPayload(partResponse).map(normalizeStationPart));

      const categoryResponse = await request({ method: 'GET', url: endpoints.categories.base });
      setCategories(getSalesArrayPayload(categoryResponse).map(normalizeStationPartCategory));

      const stationResponse = await request({ method: 'GET', url: endpoints.stations.base });
      setStations(normalizers.stations(getSalesArrayPayload(stationResponse)));

      setRules(await fetchRules());

      const fieldResponse = await request({ method: 'GET', url: endpoints.configFields.base });
      setFields(normalizers.fields(getSalesArrayPayload(fieldResponse)));

      const valueResponse = await request({ method: 'GET', url: endpoints.configFieldValues.base });
      setFieldValues(normalizers.fieldValues(getSalesArrayPayload(valueResponse)));

      const configRuleResponse = await request({
        method: 'GET',
        url: endpoints.configValueRules.base,
        params: { parentstationid: station.id },
      });
      setConfigValueRules(normalizers.valueRules(getSalesArrayPayload(configRuleResponse)));
    } catch (requestError) {
      setError(formatSalesApiError(requestError, 'Unable to load Part Auto Select data.'));
    } finally {
      setLoading(false);
    }
  }, [endpoints, fetchRules, request, station.id]);

  useEffect(() => {
    load();
  }, [load, refreshVersion]);

  useEffect(() => {
    setPartSearch('');
    setSelectedCategoryId('all');
  }, [station.id]);

  const assignedCategoryRows = useMemo(
    () => buildStationPartCategoryRows(categories, parts, assignments, station.id)
      .filter((category) => category.activeParts.length > 0),
    [assignments, categories, parts, station.id]
  );
  const assignedPartIds = useMemo(
    () => new Set(assignedCategoryRows.flatMap((category) => category.activeParts.map((part) => part.id))),
    [assignedCategoryRows]
  );
  const assignedParts = useMemo(() => assignedCategoryRows.flatMap((category) => (
    category.activeParts.map((part) => ({
      ...part,
      categoryCode: category.code,
      categoryName: category.name,
    }))
  )), [assignedCategoryRows]);
  const cellRuleKeys = useMemo(() => cellContext ? new Set([...assignedPartIds].flatMap(partId =>
    getPartMatchRuleGroups(rules, partId, station.id)
      .filter(group => isPartRuleGroupRelatedToCell(group, cellContext, fieldValues))
      .map(group => `${partId}:${group.ruleGroupId}`))) : null,
  [cellContext, assignedPartIds, rules, station.id, fieldValues]);
  const assignedRules = useMemo(
    () => rules.filter((rule) => assignedPartIds.has(rule.partId) && rule.stationId === station.id && (!cellRuleKeys || cellRuleKeys.has(`${rule.partId}:${rule.ruleGroupId}`))),
    [assignedPartIds, rules, station.id, cellRuleKeys]
  );
  const configuredPartIds = useMemo(
    () => new Set(assignedRules.map((rule) => rule.partId)),
    [assignedRules]
  );
  const configuredGroupCount = useMemo(
    () => new Set(assignedRules.map((rule) => `${rule.partId}:${rule.ruleGroupId}`)).size,
    [assignedRules]
  );
  const filteredAssignedCategoryRows = useMemo(
    () => filterPartAutoSelectCategories(assignedCategoryRows, selectedCategoryId, partSearch)
      .map(category => ({ ...category, activeParts: category.activeParts.filter(part => !cellContext || showUnconfiguredParts
        || configuredPartIds.has(part.id)) }))
      .filter(category => category.activeParts.length > 0),
    [assignedCategoryRows, partSearch, selectedCategoryId, cellContext, showUnconfiguredParts, configuredPartIds]
  );
  const filteredAssignedPartCount = useMemo(
    () => filteredAssignedCategoryRows.reduce((total, category) => total + category.activeParts.length, 0),
    [filteredAssignedCategoryRows]
  );
  const fieldById = useMemo(() => new Map(fields.map((field) => [field.id, field])), [fields]);
  const pumpQuantityFieldId = useMemo(() => fields.find((field) => (
    String(field.fieldName || '').trim().toLowerCase() === 'quantity'
  ))?.id || 0, [fields]);
  const fieldValueById = useMemo(
    () => new Map(fieldValues.map((value) => [value.id, value])),
    [fieldValues]
  );
  const stationById = useMemo(() => new Map(stations.map((sourceStation) => [
    sourceStation.id,
    sourceStation,
  ])), [stations]);
  const assignedFieldValues = useMemo(
    () => getStationAutoPickFieldValues(fieldValues, configValueRules, station.id, assignedRules),
    [assignedRules, configValueRules, fieldValues, station.id]
  );
  const assignedFieldValueIds = useMemo(
    () => new Set(assignedFieldValues.map((value) => value.id)),
    [assignedFieldValues]
  );
  const assignedFieldIds = useMemo(
    () => new Set(assignedFieldValues.map((value) => value.configFieldId)),
    [assignedFieldValues]
  );
  const selectableFields = useMemo(() => fields
    .filter((field) => ['dropdown', 'toggle'].includes(field.fieldType.toLowerCase()))
    .filter((field) => assignedFieldIds.has(field.id))
    .sort((left, right) => left.fieldName.localeCompare(right.fieldName)), [assignedFieldIds, fields]);
  const numericFields = useMemo(() => fields
    .filter((field) => isNumericFieldType(field.fieldType))
    .filter((field) => field.id !== pumpQuantityFieldId)
    .filter((field) => assignedFieldIds.has(field.id))
    .sort((left, right) => left.fieldName.localeCompare(right.fieldName)), [assignedFieldIds, fields, pumpQuantityFieldId]);
  const bulkNumericFields = useMemo(() => {
    const pumpQuantityField = fieldById.get(pumpQuantityFieldId);
    return [
      ...numericFields,
      ...(pumpQuantityField ? [pumpQuantityField] : []),
    ].sort((left, right) => left.fieldName.localeCompare(right.fieldName));
  }, [fieldById, numericFields, pumpQuantityFieldId]);
  const valuesForField = useCallback((configFieldId) => assignedFieldValues
    .filter((value) => value.configFieldId === Number(configFieldId))
    .sort((left, right) => getFieldValueLabel(left).localeCompare(getFieldValueLabel(right), undefined, { numeric: true })), [assignedFieldValues]);
  const matchFields = useMemo(() => [
    ...selectableFields.map((field) => ({ ...field, matchType: 'value', values: valuesForField(field.id) })),
    ...numericFields.map((field) => ({ ...field, matchType: 'numeric', values: [] })),
  ].sort((left, right) => left.fieldName.localeCompare(right.fieldName)), [numericFields, selectableFields, valuesForField]);
  const availableValueCount = useMemo(
    () => matchFields.reduce((total, field) => total + field.values.length, 0),
    [matchFields]
  );
  const filteredMatchFields = useMemo(() => {
    const needle = fieldBrowserSearch.trim().toLowerCase();
    return matchFields.filter((field) => {
      if (fieldBrowserType !== 'all' && field.matchType !== fieldBrowserType) return false;
      return !needle || field.fieldName.toLowerCase().includes(needle) || field.values.some(
        (value) => getFieldValueLabel(value).toLowerCase().includes(needle)
      );
    });
  }, [fieldBrowserSearch, fieldBrowserType, matchFields]);
  const selectedBrowserField = useMemo(
    () => filteredMatchFields.find((field) => field.id === selectedBrowserFieldId) || filteredMatchFields[0] || null,
    [filteredMatchFields, selectedBrowserFieldId]
  );
  const getVisiblePartRuleGroups = useCallback(
    (partId) => getPartMatchRuleGroups(rules, partId, station.id, { pumpQuantityFieldId })
      .filter(group => !cellContext || isPartRuleGroupRelatedToCell(group, cellContext, fieldValues)),
    [pumpQuantityFieldId, rules, station.id, cellContext, fieldValues]
  );
  const getStationLabel = useCallback((stationId) => (
    stationById.get(Number(stationId))?.stationName || `Station ${stationId}`
  ), [stationById]);
  const getCopySourcesForPart = useCallback((partId) => {
    const sourceStationIds = [...new Set(rules
      .filter((rule) => (
        rule.partId === Number(partId)
        && rule.stationId > 0
        && rule.stationId !== station.id
      ))
      .map((rule) => rule.stationId))]
      .sort((left, right) => getStationLabel(left).localeCompare(getStationLabel(right), undefined, { numeric: true }));
    return sourceStationIds.map((sourceStationId) => ({
      stationId: sourceStationId,
      stationName: getStationLabel(sourceStationId),
      groupCount: getPartMatchRuleGroups(rules, partId, sourceStationId, { pumpQuantityFieldId }).length,
    }));
  }, [getStationLabel, pumpQuantityFieldId, rules, station.id]);

  const openFieldBrowser = () => {
    setFieldBrowserSearch('');
    setFieldBrowserType('all');
    setSelectedBrowserFieldId(matchFields[0]?.id || 0);
    setShowFieldBrowser(true);
  };

  const openEditor = (part, options = {}) => {
    const sourceGroups = options.newOnly ? [] : options.group ? [options.group] : getVisiblePartRuleGroups(part.id);
    const targetPart = options.targetPart || part;
    const nextGlobalRuleGroupId = getNextPartRuleGroupId(rules);
    const initialGroups = sourceGroups.length ? sourceGroups.map((group) => ({
      ruleGroupId: group.ruleGroupId,
      quantity: group.quantity,
      quantityMode: group.quantityMode,
      quantitySourceRuleId: group.quantitySourceRuleId,
      conditions: group.conditions.map((rule) => makeDraftCondition(rule, fieldValueById)),
    })) : [{
      ruleGroupId: nextGlobalRuleGroupId,
      quantity: 1,
      quantityMode: PART_QUANTITY_MODE_FIXED,
      quantitySourceRuleId: 0,
      conditions: cellContext ? pumpCellConditions(cellContext.cell, cellContext.fieldIds, cellContext.selections)
        .map(condition => makeDraftCondition({ ...condition, id: 0 }, fieldValueById)) : [emptyCondition()],
    }];
    setEditor({
      part: targetPart,
      originalPartId: part.id,
      scopedBaseline: cellContext ? sourceGroups.flatMap(group => group.allConditions) : null,
      partId: targetPart.id,
      stationId: station.id,
      groups: initialGroups,
      pumpQuantityFieldId,
      nextRuleGroupId: sourceGroups.length ? nextGlobalRuleGroupId : nextGlobalRuleGroupId + 1,
    });
    setValidation('');
    setError('');
    setNotice('');
  };

  const updateGroup = (groupIndex, updater) => setEditor((current) => ({
    ...current,
    groups: current.groups.map((group, index) => (index === groupIndex ? updater(group) : group)),
  }));

  const updateCondition = (groupIndex, conditionIndex, changes) => updateGroup(groupIndex, (group) => ({
    ...group,
    conditions: group.conditions.map((condition, index) => (
      index === conditionIndex ? { ...condition, ...changes } : condition
    )),
  }));

  const addCondition = (groupIndex) => updateGroup(groupIndex, (group) => ({
    ...group,
    conditions: [...group.conditions, emptyCondition()],
  }));

  const removeCondition = (groupIndex, conditionIndex) => updateGroup(groupIndex, (group) => ({
    ...group,
    conditions: group.conditions.filter((condition, index) => index !== conditionIndex),
  }));

  const addGroup = () => setEditor((current) => {
    const ruleGroupId = current.nextRuleGroupId || getNextPartRuleGroupId(current.groups);
    return {
      ...current,
      groups: [...current.groups, {
        ruleGroupId,
        quantity: 1,
        quantityMode: PART_QUANTITY_MODE_FIXED,
        quantitySourceRuleId: 0,
        conditions: cellContext ? pumpCellConditions(cellContext.cell, cellContext.fieldIds, cellContext.selections)
          .map(condition => makeDraftCondition({ ...condition, id: 0 }, fieldValueById)) : [emptyCondition()],
      }],
      nextRuleGroupId: ruleGroupId + 1,
    };
  });

  const removeGroup = (groupIndex) => setEditor((current) => ({
    ...current,
    groups: current.groups.filter((group, index) => index !== groupIndex),
  }));

  const conditionText = (rule) => {
    if (rule.requiredValueId) {
      const value = fieldValueById.get(rule.requiredValueId);
      const unavailable = !assignedFieldValueIds.has(rule.requiredValueId) ? ` (not assigned to ${station.stationName})` : '';
      return `${fieldById.get(value?.configFieldId)?.fieldName || 'Value'} is ${getFieldValueLabel(value)}${unavailable}`;
    }
    if (!rule.fieldId) return 'Always selected (default)';
    const bounds = [
      rule.minValue !== null ? `at least ${rule.minValue}` : '',
      rule.maxValue !== null ? `at most ${rule.maxValue}` : '',
    ].filter(Boolean).join(' and ');
    return `${fieldById.get(rule.fieldId)?.fieldName || `Field ${rule.fieldId}`} is ${bounds || 'within range'}`;
  };

  const verifyOperation = useCallback(async (operation) => {
    const confirmedRules = await fetchRules();
    if (operation.method === 'DELETE') {
      return !confirmedRules.some((rule) => rule.id === Number(operation.payload.Id));
    }
    if (operation.method === 'PUT') {
      const id = Number(operation.payload.Id);
      const existing = confirmedRules.find((rule) => rule.id === id);
      return Boolean(existing && findMatchingPartRule([existing], operation.payload));
    }
    return Boolean(findMatchingPartRule(confirmedRules, operation.payload));
  }, [fetchRules]);

  const copyRulesFromStation = async (part, sourceStationId) => {
    const sourceLabel = getStationLabel(sourceStationId);
    const currentGroups = getPartMatchRuleGroups(rules, part.id, station.id, { pumpQuantityFieldId });
    if (currentGroups.length > 0) {
      setError(`${part.partNumber || part.name} already has rules for ${station.stationName}. Remove or edit those rules before copying from ${sourceLabel}.`);
      return;
    }

    const draft = buildPartMatchRuleCopyDraft(
      rules,
      part.id,
      sourceStationId,
      station.id,
      getNextPartRuleGroupId(rules),
      pumpQuantityFieldId
    );
    if (draft.groups.length === 0) {
      setError(`No Auto Pick rules were found for ${part.partNumber || part.name} on ${sourceLabel}.`);
      return;
    }

    const unavailableValueCondition = draft.groups
      .flatMap((group) => group.conditions)
      .find((condition) => condition.type === 'value'
        && Number(condition.requiredValueId) > 0
        && !assignedFieldValueIds.has(Number(condition.requiredValueId)));
    if (unavailableValueCondition) {
      const unavailableValue = fieldValueById.get(Number(unavailableValueCondition.requiredValueId));
      setError(`${getFieldValueLabel(unavailableValue)} is not available to ${station.stationName}. Assign the matching field value in Step 5 before copying from ${sourceLabel}.`);
      return;
    }

    const unavailableNumericCondition = draft.groups
      .flatMap((group) => group.conditions)
      .find((condition) => condition.type === 'numeric'
        && Number(condition.fieldId) > 0
        && !assignedFieldIds.has(Number(condition.fieldId)));
    if (unavailableNumericCondition) {
      setError(`${fieldById.get(Number(unavailableNumericCondition.fieldId))?.fieldName || `Field ${unavailableNumericCondition.fieldId}`} is not available to ${station.stationName}. Assign the numeric field in Step 5 before copying from ${sourceLabel}.`);
      return;
    }

    const validationMessage = validatePartMatchRuleDraft(draft);
    if (validationMessage) {
      setError(validationMessage);
      return;
    }

    const operations = buildPartMatchRuleOperations(draft, []);
    setSaving(true);
    setError('');
    setNotice('');
    try {
      for (const operation of operations) {
        try {
          await request({
            method: operation.method,
            url: endpoints.partMatchRules.base,
            data: operation.payload,
            contract: { requestCase: 'raw' },
            requestPolicy: { retry: false, timeoutMs: 20000 },
          });
        } catch (requestError) {
          let wasCommitted = false;
          try {
            wasCommitted = await verifyOperation(operation);
          } catch {
            // Preserve the original write error if verification is unavailable.
          }
          if (!wasCommitted) throw requestError;
        }
      }
      const nextRules = await fetchRules();
      setRules(nextRules);
      const copiedGroupCount = getPartMatchRuleGroups(nextRules, part.id, station.id, { pumpQuantityFieldId }).length;
      setNotice(`${part.partNumber || part.name} copied ${copiedGroupCount || draft.groups.length} OR rule ${copiedGroupCount === 1 || draft.groups.length === 1 ? 'group' : 'groups'} from ${sourceLabel} to ${station.stationName}.`);
    } catch (requestError) {
      try {
        setRules(await fetchRules());
      } catch {
        // Keep the last confirmed state when recovery refresh is unavailable.
      }
      setError(formatSalesApiError(requestError, `Unable to copy Auto Pick rules from ${sourceLabel}.`));
    } finally {
      setSaving(false);
    }
  };

  const saveRules = async () => {
    if (cellContext?.allowedCategoryCode && !assignedParts.some(part => part.id === editor.partId && isPartInCategory(part, categories, cellContext.allowedCategoryCode))) {
      setValidation(`Pump-table rules must select a station-assigned part in category ${cellContext.allowedCategoryCode}.`);
      return;
    }
    const unavailableValueCondition = editor.groups
      .flatMap((group) => group.conditions)
      .find((condition) => condition.type === 'value'
        && Number(condition.requiredValueId) > 0
        && !assignedFieldValueIds.has(Number(condition.requiredValueId)));
    if (unavailableValueCondition) {
      const unavailableValue = fieldValueById.get(Number(unavailableValueCondition.requiredValueId));
      setValidation(`${getFieldValueLabel(unavailableValue)} is not available to ${station.stationName}. Choose an available value or remove this condition.`);
      return;
    }
    const validationMessage = validatePartMatchRuleDraft(editor);
    if (validationMessage) {
      setValidation(validationMessage);
      return;
    }

    const existingRows = editor.scopedBaseline || rules.filter((rule) => (
      rule.partId === editor.partId
      && rule.stationId === station.id
    ));
    const operations = buildPartMatchRuleOperations(editor, existingRows);
    if (!operations.length) {
      setEditor(null);
      return;
    }

    setSaving(true);
    setValidation('');
    setError('');
    try {
      if (cellContext) {
        if (cellContext.allowedCategoryCode) {
          const currentParts = getSalesArrayPayload(await request({ method: 'GET', url: endpoints.parts.base })).map(normalizeStationPart);
          const currentCategories = getSalesArrayPayload(await request({ method: 'GET', url: endpoints.categories.base })).map(normalizeStationPartCategory);
          const currentAssignments = getSalesArrayPayload(await request({ method: 'GET', url: endpoints.partStations.base, params: { stationid: station.id } })).map(normalizeStationPartAssignment);
          if (!isPartInCategory(currentParts.find(part => part.id === editor.partId), currentCategories, cellContext.allowedCategoryCode)
            || !currentAssignments.some(row => row.stationId === station.id && row.partId === editor.partId)) throw new Error(`The selected part is no longer an assigned category ${cellContext.allowedCategoryCode} part. Reopen the editor.`);
        }
        const fresh = await fetchRules();
        const baselineIds = new Set(existingRows.map(row => row.id));
        const comparable = rows => JSON.stringify([...rows].sort((a, b) => a.id - b.id));
        const baselineGroupIds = new Set(existingRows.map(row => row.ruleGroupId));
        const currentScope = fresh.filter(row => row.stationId === station.id && row.partId === editor.originalPartId && baselineGroupIds.has(row.ruleGroupId));
        if (comparable(currentScope) !== comparable(existingRows)) throw new Error('This rule group changed on the server. Close and reopen the editor before saving.');
        if (editor.groups.some(group => !baselineGroupIds.has(Number(group.ruleGroupId)) && fresh.some(row => row.ruleGroupId === Number(group.ruleGroupId) && !baselineIds.has(row.id)))) throw new Error('A new rule group ID is already in use. Close and reopen the editor to allocate the next ID.');
        const targetRows = fresh.filter(row => row.stationId === station.id && row.partId === editor.partId && !baselineIds.has(row.id));
        if (editor.groups.some(group => targetRows.some(row => row.ruleGroupId === Number(group.ruleGroupId)))) throw new Error('The selected part already uses this group ID. Choose another part or review that existing group first.');
        const signature = rows => rows.map(row => JSON.stringify([row.requiredValueId, row.fieldId, row.minValue, row.maxValue])).sort().join('|');
        const targetGroups = getPartMatchRuleGroups(targetRows, editor.partId, station.id);
        for (const group of editor.groups) {
          const candidate = buildPartMatchRuleOperations({ ...editor, groups: [group] }, [])
            .map(operation => normalizePartMatchRule(operation.payload));
          if (targetGroups.some(target => signature(target.allConditions) === signature(candidate))) throw new Error('The selected part already has a group with these conditions. Edit that group instead of adding a duplicate.');
        }
      }
      for (const operation of operations) {
        try {
          await request({
            method: operation.method,
            url: endpoints.partMatchRules.base,
            data: operation.payload,
            contract: { requestCase: 'raw' },
            requestPolicy: { retry: false, timeoutMs: 20000 },
          });
        } catch (requestError) {
          // A timed-out write can still commit. Confirm the exact row state
          // before reporting failure; never blindly retry a non-idempotent POST.
          let wasCommitted = false;
          try {
            wasCommitted = await verifyOperation(operation);
          } catch {
            // Preserve the original write error if verification is unavailable.
          }
          if (!wasCommitted) throw requestError;
        }
      }
      const nextRules = await fetchRules();
      setRules(nextRules);
      onRulesChanged?.();
      const groupCount = getPartMatchRuleGroups(nextRules, editor.partId, station.id, { pumpQuantityFieldId }).length;
      setNotice(groupCount
        ? `${editor.part.partNumber || editor.part.name} now has ${groupCount} OR rule ${groupCount === 1 ? 'group' : 'groups'}.`
        : `Auto Select rules were removed from ${editor.part.partNumber || editor.part.name}.`);
      setEditor(null);
    } catch (requestError) {
      try {
        setRules(await fetchRules());
      } catch {
        // Keep the last confirmed state when recovery refresh is unavailable.
      }
      setError(formatSalesApiError(requestError, 'Unable to save all Part Auto Select rules. Confirmed changes were retained; review the rule groups and try again.'));
    } finally {
      setSaving(false);
    }
  };

  const saveBulkRules = async ({ rows, baselineRules }) => {
    setSaving(true);
    setBulkValidation('');
    setError('');
    try {
      const latestRules = await fetchRules();
      const latestAssignmentsResponse = await request({
        method: 'GET',
        url: endpoints.partStations.base,
        params: { stationid: station.id },
      });
      const latestAssignments = getSalesArrayPayload(latestAssignmentsResponse)
        .map(normalizeStationPartAssignment)
        .filter((assignment) => assignment.stationId === station.id);
      const latestAssignedPartIds = new Set(latestAssignments.map((assignment) => assignment.partId));
      const assignmentChanged = assignedPartIds.size !== latestAssignedPartIds.size
        || [...assignedPartIds].some((partId) => !latestAssignedPartIds.has(partId));
      if (assignmentChanged) {
        setBulkValidation('Parts assigned to this station changed after the bulk editor was opened. Close and reopen it before saving.');
        return;
      }

      const latestScopedRules = getPartAutoBulkScopeRules(latestRules, assignedPartIds, station.id);
      const snapshotConflict = getPartAutoBulkSnapshotConflict(baselineRules, latestScopedRules);
      if (snapshotConflict) {
        setBulkValidation(snapshotConflict);
        return;
      }

      const latestFieldResponse = await request({ method: 'GET', url: endpoints.configFields.base });
      const latestFields = normalizers.fields(getSalesArrayPayload(latestFieldResponse));
      const latestValueResponse = await request({ method: 'GET', url: endpoints.configFieldValues.base });
      const latestValues = normalizers.fieldValues(getSalesArrayPayload(latestValueResponse));
      const latestValueRuleResponse = await request({
        method: 'GET',
        url: endpoints.configValueRules.base,
        params: { parentstationid: station.id },
      });
      const latestValueRules = normalizers.valueRules(getSalesArrayPayload(latestValueRuleResponse));
      const latestAssignedValues = getStationAutoPickFieldValues(
        latestValues,
        latestValueRules,
        station.id,
        latestScopedRules
      );
      const latestAssignedFieldIds = new Set(latestAssignedValues.map((value) => value.configFieldId));
      const latestNumericFields = latestFields.filter((field) => (
        isNumericFieldType(field.fieldType)
        && (
          latestAssignedFieldIds.has(field.id)
          || String(field.fieldName || '').trim().toLowerCase() === 'quantity'
        )
      ));
      const latestAnalysis = analyzePartAutoBulkRows({
        rows,
        baselineRules,
        allRules: latestRules,
        assignedParts,
        assignedFieldValues: latestAssignedValues,
        numericFields: latestNumericFields,
        stationId: station.id,
      });
      if (latestAnalysis.errorCount > 0) {
        setBulkValidation('The latest API data makes one or more staged Part Auto Pick rules invalid. Close and reopen the bulk editor, then review the highlighted rows.');
        return;
      }
      if (latestAnalysis.operations.length === 0) return;

      let completedCount = 0;
      for (const operation of latestAnalysis.operations) {
        try {
          await request({
            method: operation.method,
            url: endpoints.partMatchRules.base,
            data: operation.payload,
            contract: { requestCase: 'raw' },
            requestPolicy: { retry: false, timeoutMs: 20000 },
          });
        } catch (requestError) {
          let wasCommitted = false;
          try {
            wasCommitted = await verifyOperation(operation);
          } catch {
            // Preserve the original write error if verification is unavailable.
          }
          if (!wasCommitted) {
            requestError.completedCount = completedCount;
            throw requestError;
          }
        }
        completedCount += 1;
      }

      setRules(await fetchRules());
      setNotice(`${latestAnalysis.createCount} Part Auto Pick rule row(s) created and ${latestAnalysis.updateCount} row(s) updated successfully.`);
      setBulkEditorOpen(false);
    } catch (requestError) {
      try {
        setRules(await fetchRules());
      } catch {
        // Keep the last confirmed state when recovery refresh is unavailable.
      }
      const progress = requestError.completedCount > 0
        ? ` ${requestError.completedCount} row(s) were saved before the error; close and reopen the bulk editor to review the latest server state.`
        : '';
      setBulkValidation(`${formatSalesApiError(requestError, 'Unable to save bulk Part Auto Pick rules.')}${progress}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="sales-loading"><Spinner animation="border" size="sm" /> Loading Part Auto Select rules for {station.stationName}...</div>;
  }

  return (
    <>
      {cellContext && <>
        {error && <Alert variant="danger">{error}</Alert>}
        {notice && <Alert variant="success">{notice}</Alert>}
        {cellContext.allowedCategoryCode && <Alert variant="info">This pump table shows only category {cellContext.allowedCategoryCode} parts and rules. Other categories remain available in Step 7.</Alert>}
        <PumpCellRuleRows station={station} context={cellContext} parts={assignedParts.filter(part => !cellContext.allowedCategoryCode || isPartInCategory(part, categories, cellContext.allowedCategoryCode))}
          groups={assignedParts.filter(part => !cellContext.allowedCategoryCode || isPartInCategory(part, categories, cellContext.allowedCategoryCode)).flatMap(part => getVisiblePartRuleGroups(part.id).map(group => ({ part, group })))}
          conditionText={conditionText} canEdit={canEditSales && !saving}
          onCreate={part => openEditor(part, { newOnly: true })}
          onEdit={(part, group, targetPart) => openEditor(part, { group, targetPart })} />
      </>}
      {!cellContext && <>
      <div className="config-admin-page-heading">
        <div>
          <h4>Part Auto Select for {station.stationName}</h4>
          <p>Define when each assigned part is selected. Conditions inside a group are AND; separate groups are OR.</p>
        </div>
        {!cellContext && <Button
          variant="outline-primary"
          onClick={() => { setBulkValidation(''); setBulkEditorOpen(true); }}
          disabled={!canEditSales || assignedParts.length === 0}
        >
          <i className="bi bi-table me-2" aria-hidden="true"></i>Bulk edit / upload
        </Button>}
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {notice && <Alert variant="success" dismissible onClose={() => setNotice('')}>{notice}</Alert>}
      {cellContext && <Form.Check className="mb-3" id="cell-show-available-parts" label="Show all assigned parts to add new rules" checked={showUnconfiguredParts} onChange={event => setShowUnconfiguredParts(event.target.checked)} />}

      <div className="config-admin-parts-summary">
        <span><strong>{assignedPartIds.size}</strong> eligible parts</span>
        <span><strong>{selectableFields.length + numericFields.length}</strong> available fields</span>
        <span><strong>{configuredPartIds.size}</strong> configured parts</span>
        <span><strong>{configuredGroupCount}</strong> OR rule groups</span>
      </div>

      <div className="config-admin-auto-field-summary">
        <div>
          <span className="config-admin-auto-field-icon"><i className="bi bi-ui-checks-grid" aria-hidden="true"></i></span>
          <div>
            <strong>Available match fields</strong>
            <small>
              {selectableFields.length} selected-value {selectableFields.length === 1 ? 'field' : 'fields'} with {availableValueCount} available {availableValueCount === 1 ? 'value' : 'values'} · {numericFields.length} numeric {numericFields.length === 1 ? 'field' : 'fields'}
            </small>
          </div>
        </div>
        <Button variant="outline-primary" size="sm" onClick={openFieldBrowser} disabled={matchFields.length === 0}>
          Browse fields and values
        </Button>
      </div>

      {assignedCategoryRows.length === 0 ? (
        <div className="config-admin-empty">No parts are assigned to this station. Assign parts in Step 6 before building Auto Select rules.</div>
      ) : (
        <>
          <div className="config-admin-part-category-toolbar config-admin-auto-part-toolbar">
            <Form.Group>
              <Form.Label>Search parts or categories</Form.Label>
              <Form.Control
                type="search"
                placeholder="Part number, part name, category code, or category name"
                value={partSearch}
                onChange={(event) => setPartSearch(event.target.value)}
              />
              <Form.Text>
                {filteredAssignedPartCount.toLocaleString()} matching {filteredAssignedPartCount === 1 ? 'part' : 'parts'} in {filteredAssignedCategoryRows.length} {filteredAssignedCategoryRows.length === 1 ? 'category' : 'categories'}
              </Form.Text>
            </Form.Group>
            <Form.Group>
              <Form.Label>Category</Form.Label>
              <Form.Select value={selectedCategoryId} onChange={(event) => setSelectedCategoryId(event.target.value)}>
                <option value="all">All assigned categories ({assignedCategoryRows.length})</option>
                {assignedCategoryRows.map((category) => (
                  <option key={category.id || 'uncategorized'} value={category.id}>
                    {category.code ? `${category.code} - ` : ''}{category.name} ({category.activeParts.length})
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>
          {filteredAssignedCategoryRows.length === 0 ? (
            <div className="config-admin-empty">{cellContext ? 'No AutoPick groups overlap this cell with the current filters. Enable “Show all assigned parts” to add rules.' : 'No assigned parts or categories match the current filters.'}</div>
          ) : (
            <div className="config-admin-auto-categories">
              {filteredAssignedCategoryRows.map((category) => {
                const sourceCategory = assignedCategoryRows.find((candidate) => candidate.id === category.id) || category;
                const configuredCount = sourceCategory.activeParts.filter((part) => configuredPartIds.has(part.id)).length;
                return (
                  <Card key={category.id || 'uncategorized'} className="config-admin-auto-category">
                    <Card.Header>
                      <div><strong>{category.code ? `${category.code} - ` : ''}{category.name}</strong>{category.description && category.description !== category.name && <small>{category.description}</small>}</div>
                      <Badge bg={configuredCount === sourceCategory.activeParts.length ? 'success' : configuredCount ? 'primary' : 'secondary'}>
                        {configuredCount} of {sourceCategory.activeParts.length} configured
                      </Badge>
                    </Card.Header>
                    <div className="table-responsive">
                      <Table hover className="mb-0 config-admin-auto-table">
                        <thead><tr><th>Part</th><th>Auto Select logic</th><th>Actions</th></tr></thead>
                        <tbody>
                          {category.activeParts.map((part) => {
                            const groups = getVisiblePartRuleGroups(part.id);
                            const copySources = !cellContext && groups.length === 0 ? getCopySourcesForPart(part.id) : [];
                            return (
                              <tr key={part.id}>
                                <td><strong>{part.partNumber || `Part ${part.id}`}</strong>{part.name && <small>{part.name}</small>}</td>
                                <td>
                                  {groups.length === 0 ? <span className="text-muted">No automatic match rules</span> : (
                                    <div className="config-admin-auto-expression">
                                      {groups.map((group, groupIndex) => (
                                        <React.Fragment key={group.ruleGroupId}>
                                          {groupIndex > 0 && <span className="config-admin-rule-or">OR</span>}
                                          <span className="config-admin-auto-group">
                                            <Badge className="config-admin-auto-group-id" bg="success">Group {group.ruleGroupId}</Badge>
                                            <strong className="config-admin-auto-quantity">
                                              {group.quantityMode === PART_QUANTITY_MODE_PUMP_ROWS
                                                ? `Pump QTY × ${group.quantity}`
                                                : `Qty ${group.quantity}`}
                                            </strong>
                                            {group.quantityMode === PART_QUANTITY_MODE_PUMP_ROWS && group.conditions.length === 0 && (
                                              <span>All pump rows</span>
                                            )}
                                            {group.conditions.map((rule, conditionIndex) => (
                                              <React.Fragment key={rule.id}>
                                                {conditionIndex > 0 && <em>AND</em>}
                                                <span>{conditionText(rule)}</span>
                                              </React.Fragment>
                                            ))}
                                          </span>
                                        </React.Fragment>
                                      ))}
                                    </div>
                                  )}
                                </td>
                                <td>
                                  <div className="d-flex flex-wrap gap-2">
                                    <Button size="sm" onClick={() => openEditor(part)} disabled={!canEditSales || saving}>{groups.length ? 'Edit rules' : 'Add rules'}</Button>
                                    {copySources.length > 0 && (
                                      <Dropdown>
                                        <Dropdown.Toggle size="sm" variant="outline-primary" disabled={!canEditSales || saving}>
                                          Copy from
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu>
                                          {copySources.map((source) => (
                                            <Dropdown.Item
                                              key={source.stationId}
                                              onClick={() => copyRulesFromStation(part, source.stationId)}
                                            >
                                              {source.stationName} ({source.groupCount} {source.groupCount === 1 ? 'group' : 'groups'})
                                            </Dropdown.Item>
                                          ))}
                                        </Dropdown.Menu>
                                      </Dropdown>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      </>}
      <Modal show={showFieldBrowser} onHide={() => setShowFieldBrowser(false)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>Available match fields</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="config-admin-auto-browser-intro">Browse the configuration fields that can be used to build Part Auto Select conditions. Values assigned in Step 5 or already used by {station.stationName}'s current Auto Pick rules are available.</p>
          <div className="config-admin-auto-browser-filters">
            <Form.Group>
              <Form.Label>Search fields or values</Form.Label>
              <Form.Control
                type="search"
                placeholder="Field name or possible value"
                value={fieldBrowserSearch}
                onChange={(event) => setFieldBrowserSearch(event.target.value)}
                autoFocus
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Match type</Form.Label>
              <Form.Select value={fieldBrowserType} onChange={(event) => setFieldBrowserType(event.target.value)}>
                <option value="all">All match fields</option>
                <option value="value">Selected values</option>
                <option value="numeric">Numeric ranges</option>
              </Form.Select>
            </Form.Group>
          </div>
          <div className="config-admin-auto-field-browser">
            <div className="config-admin-auto-browser-list" aria-label="Available match field list">
              <div className="config-admin-auto-browser-list-heading">
                <strong>{filteredMatchFields.length} matching {filteredMatchFields.length === 1 ? 'field' : 'fields'}</strong>
              </div>
              {filteredMatchFields.length === 0 ? (
                <div className="config-admin-empty">No fields or values match the current filters.</div>
              ) : filteredMatchFields.map((field) => (
                <button
                  type="button"
                  key={field.id}
                  className={selectedBrowserField?.id === field.id ? 'active' : ''}
                  onClick={() => setSelectedBrowserFieldId(field.id)}
                >
                  <span><strong>{field.fieldName}</strong><small>{field.fieldType} · {field.scope || 'All scopes'}</small></span>
                  <Badge bg={field.matchType === 'value' ? 'primary' : 'success'}>
                    {field.matchType === 'value' ? `${field.values.length} ${field.values.length === 1 ? 'value' : 'values'}` : 'Numeric'}
                  </Badge>
                </button>
              ))}
            </div>
            <section className="config-admin-auto-browser-detail" aria-live="polite">
              {!selectedBrowserField ? (
                <div className="config-admin-empty">Select a field to see its matching details.</div>
              ) : (
                <>
                  <div className="config-admin-auto-browser-detail-heading">
                    <div><span>{selectedBrowserField.matchType === 'value' ? 'Selected value field' : 'Numeric range field'}</span><h5>{selectedBrowserField.fieldName}</h5></div>
                    <Badge bg="light" text="dark">ID {selectedBrowserField.id}</Badge>
                  </div>
                  {selectedBrowserField.matchType === 'value' ? (
                    <>
                      <p>Choose one of these values when adding a selected-value condition.</p>
                      <div className="config-admin-auto-browser-values">
                        {selectedBrowserField.values.map((value) => (
                          <div key={value.id}><strong>{getFieldValueLabel(value)}</strong><small>Value ID {value.id}</small></div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="config-admin-auto-browser-numeric">
                      <i className="bi bi-sliders" aria-hidden="true"></i>
                      <div><strong>Numeric range condition</strong><p>Set a minimum, maximum, both, or leave both open. Minimum and maximum comparisons are inclusive.</p></div>
                    </div>
                  )}
                </>
              )}
            </section>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setShowFieldBrowser(false)}>Done</Button>
        </Modal.Footer>
      </Modal>

      <PartAutoSelectBulkEditor
        show={bulkEditorOpen}
        station={station}
        baselineRules={assignedRules}
        allRules={rules}
        assignedParts={assignedParts}
        assignedFieldValues={assignedFieldValues}
        referenceFields={fields}
        numericFields={bulkNumericFields}
        saving={saving}
        validation={bulkValidation}
        onClose={() => { setBulkEditorOpen(false); setBulkValidation(''); }}
        onSave={saveBulkRules}
      />

      <Modal show={Boolean(editor)} onHide={saving ? undefined : () => setEditor(null)} size="xl" centered scrollable>
        {editor && (
          <>
            <Modal.Header closeButton={!saving}>
              <Modal.Title>Auto Select rules: {editor.part.partNumber || editor.part.name || `Part ${editor.part.id}`}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Alert variant="info">
                Every condition inside a group must match (<strong>AND</strong>). The part is selected when any complete group matches (<strong>OR</strong>).
                {' '}An <strong>Always selected</strong> group has no field conditions and makes the part a default for this station.
                {' '}Quantity can be fixed or calculated from matching Pump Configuration rows. Pump-derived groups add their matching row quantities together, so separate Main and Jockey groups can produce Main only, Jockey only, or Main + Jockey totals.
                {' '}The group quantity is used as a per-pump multiplier in that mode; 0 is saved as null.
                {' '}Rule group IDs increment across all parts and station types.
              </Alert>
              {validation && <Alert variant="danger">{validation}</Alert>}
              {cellContext && <Alert variant="warning">Editing an existing group changes it everywhere it applies, including other matrix cells. Only groups shown when this editor opened are saved; unrelated groups are preserved.</Alert>}
              {cellContext && error && <Alert variant="danger">{error}</Alert>}
              <div className="config-admin-auto-editor-groups">
                {editor.groups.map((group, groupIndex) => (
                  <React.Fragment key={`${group.ruleGroupId}-${groupIndex}`}>
                    {groupIndex > 0 && <div className="config-admin-auto-or-divider"><span>OR</span></div>}
                    <Card className="config-admin-rule-group">
                      <Card.Header className="config-admin-rule-group-header">
                        <div><h5>Rule group {group.ruleGroupId}</h5><small>All conditions below must match. {group.quantityMode === PART_QUANTITY_MODE_PUMP_ROWS ? 'Each matching pump row contributes its QTY.' : 'The fixed quantity applies when this group matches.'}</small></div>
                        <div className="config-admin-auto-group-actions">
                          <Form.Group>
                            <Form.Label>Group ID</Form.Label>
                            <Form.Control
                              type="number"
                              value={group.ruleGroupId}
                              aria-label={`Rule group ${group.ruleGroupId} ID`}
                              readOnly
                            />
                            <Form.Text>Assigned automatically from the global sequence</Form.Text>
                          </Form.Group>
                          <Form.Group>
                            <Form.Label>Quantity calculation</Form.Label>
                            <Form.Select
                              value={group.quantityMode || PART_QUANTITY_MODE_FIXED}
                              aria-label={`Rule group ${group.ruleGroupId} quantity calculation`}
                              onChange={(event) => {
                                const quantityMode = event.target.value;
                                updateGroup(groupIndex, (current) => ({
                                  ...current,
                                  quantityMode,
                                  conditions: quantityMode === PART_QUANTITY_MODE_PUMP_ROWS
                                    && current.conditions.length === 1
                                    && current.conditions[0].type === 'default'
                                    ? []
                                    : quantityMode === PART_QUANTITY_MODE_FIXED && current.conditions.length === 0
                                      ? [{ ...emptyCondition(), type: 'default' }]
                                      : current.conditions,
                                }));
                              }}
                            >
                              <option value={PART_QUANTITY_MODE_FIXED}>Fixed quantity</option>
                              <option value={PART_QUANTITY_MODE_PUMP_ROWS} disabled={!pumpQuantityFieldId}>Matching pump row quantities</option>
                            </Form.Select>
                            <Form.Text>{group.quantityMode === PART_QUANTITY_MODE_PUMP_ROWS ? 'Adds QTY from every pump row matching this group' : 'Uses one fixed output quantity'}</Form.Text>
                          </Form.Group>
                          <Form.Group>
                            <Form.Label>{group.quantityMode === PART_QUANTITY_MODE_PUMP_ROWS ? 'Per-pump multiplier' : 'Quantity'}</Form.Label>
                            <Form.Control
                              type="number"
                              min="0"
                              step="1"
                              value={group.quantity}
                              aria-label={`Rule group ${group.ruleGroupId} quantity`}
                              onChange={(event) => updateGroup(groupIndex, (current) => ({
                                ...current,
                                quantity: event.target.value,
                              }))}
                              required
                            />
                            <Form.Text>{group.quantityMode === PART_QUANTITY_MODE_PUMP_ROWS ? '1 returns the exact matching pump QTY' : '0 saves as null; applied to every row'}</Form.Text>
                          </Form.Group>
                          <Button variant="outline-danger" size="sm" onClick={() => removeGroup(groupIndex)}>Remove group</Button>
                        </div>
                      </Card.Header>
                      <Card.Body>
                        {group.conditions.length === 0 && (
                          <div className="config-admin-empty">
                            {group.quantityMode === PART_QUANTITY_MODE_PUMP_ROWS
                              ? 'All pump rows contribute their QTY. Add a condition to limit this group to specific pumps.'
                              : 'This group has no conditions.'}
                          </div>
                        )}
                        {group.conditions.map((condition, conditionIndex) => (
                          <React.Fragment key={condition.key}>
                            {conditionIndex > 0 && <div className="config-admin-auto-and-divider"><span>AND</span></div>}
                            <div className="config-admin-auto-condition-editor">
                              <Row className="g-3 align-items-end">
                                <Col lg={3}>
                                  <Form.Group>
                                    <Form.Label>Condition type</Form.Label>
                                    <Form.Select
                                      value={condition.type}
                                      onChange={(event) => updateCondition(groupIndex, conditionIndex, {
                                        type: event.target.value,
                                        configFieldId: 0,
                                        requiredValueId: 0,
                                        fieldId: 0,
                                        minValue: '',
                                        maxValue: '',
                                      })}
                                    >
                                      <option value="value">Selected value</option>
                                      <option value="numeric">Numeric range</option>
                                      <option value="default">Always selected (default)</option>
                                    </Form.Select>
                                  </Form.Group>
                                </Col>
                                {condition.type === 'default' ? (
                                  <Col lg={7}>
                                    <Alert variant="secondary" className="mb-0 py-2">
                                      No field or value is required. This rule group always matches.
                                    </Alert>
                                  </Col>
                                ) : condition.type === 'value' ? (
                                  <>
                                    <Col lg={3}>
                                      <Form.Group>
                                        <Form.Label>Configuration field</Form.Label>
                                        <Form.Select
                                          value={condition.configFieldId}
                                          onChange={(event) => updateCondition(groupIndex, conditionIndex, { configFieldId: Number(event.target.value), requiredValueId: 0 })}
                                        >
                                          <option value={0}>Choose field...</option>
                                          {selectableFields.map((field) => <option key={field.id} value={field.id}>{field.fieldName}</option>)}
                                        </Form.Select>
                                      </Form.Group>
                                    </Col>
                                    <Col lg={4}>
                                      <Form.Group>
                                        <Form.Label>Required selected value</Form.Label>
                                        <Form.Select
                                          value={condition.requiredValueId}
                                          disabled={!condition.configFieldId}
                                          onChange={(event) => updateCondition(groupIndex, conditionIndex, { requiredValueId: Number(event.target.value) })}
                                        >
                                          <option value={0}>Choose value...</option>
                                          {valuesForField(condition.configFieldId).map((value) => <option key={value.id} value={value.id}>{getFieldValueLabel(value)}</option>)}
                                        </Form.Select>
                                      </Form.Group>
                                    </Col>
                                  </>
                                ) : (
                                  <>
                                    <Col lg={3}>
                                      <Form.Group>
                                        <Form.Label>Numeric field</Form.Label>
                                        <Form.Select value={condition.fieldId} onChange={(event) => updateCondition(groupIndex, conditionIndex, { fieldId: Number(event.target.value) })}>
                                          <option value={0}>Choose field...</option>
                                          {numericFields.map((field) => <option key={field.id} value={field.id}>{field.fieldName}</option>)}
                                        </Form.Select>
                                      </Form.Group>
                                    </Col>
                                    <Col lg={2}>
                                      <Form.Group><Form.Label>Minimum</Form.Label><Form.Control type="number" step="0.01" placeholder="No minimum" value={condition.minValue} onChange={(event) => updateCondition(groupIndex, conditionIndex, { minValue: event.target.value })} /></Form.Group>
                                    </Col>
                                    <Col lg={2}>
                                      <Form.Group><Form.Label>Maximum</Form.Label><Form.Control type="number" step="0.01" placeholder="No maximum" value={condition.maxValue} onChange={(event) => updateCondition(groupIndex, conditionIndex, { maxValue: event.target.value })} /></Form.Group>
                                    </Col>
                                  </>
                                )}
                                <Col lg={2} className="text-end">
                                  <Button variant="outline-danger" size="sm" onClick={() => removeCondition(groupIndex, conditionIndex)}>Remove</Button>
                                </Col>
                              </Row>
                            </div>
                          </React.Fragment>
                        ))}
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="mt-3"
                          onClick={() => addCondition(groupIndex)}
                          disabled={group.conditions.some((condition) => condition.type === 'default')}
                          title={group.conditions.some((condition) => condition.type === 'default') ? 'An always-selected group cannot have AND conditions.' : undefined}
                        >
                          <i className="bi bi-plus-circle me-2" aria-hidden="true"></i>Add AND condition
                        </Button>
                      </Card.Body>
                    </Card>
                  </React.Fragment>
                ))}
              </div>
              <Button variant="outline-primary" className="mt-3" onClick={addGroup}>
                <i className="bi bi-plus-circle me-2" aria-hidden="true"></i>Add OR rule group
              </Button>
            </Modal.Body>
            <Modal.Footer>
              {editor.groups.length === 0 && <small className="me-auto text-danger">Saving with no groups removes all rules for this part.</small>}
              <Button variant="outline-secondary" onClick={() => setEditor(null)} disabled={saving}>Cancel</Button>
              <Button onClick={saveRules} disabled={saving}>
                {saving && <Spinner animation="border" size="sm" className="me-2" />}
                Save rules
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </>
  );
};

export default PartAutoSelectManager;
